import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { validateVideoFile, captureFrames } from "./videoFile";
import VideoPosterModal from "./VideoPosterModal";

const releaseFrames = (frames) => frames.forEach((f) => URL.revokeObjectURL(f.url));

// Section A, before any upload: check the file in the browser, then let the user pick
// the cover from several frames. Nothing is uploaded until onConfirm is called.
export default function useVideoPicker({ onConfirm }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const runRef = useRef(0);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);   // null | { file, duration, frames, selected, loading }

  const close = () => {
    runRef.current += 1;                       // ignore a frame capture that is still running
    if (modal) releaseFrames(modal.frames);
    setModal(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;

    setError(null);
    const check = await validateVideoFile(file);
    if (!check.ok) {
      setError(t(`video.errors.${check.reason}`));
      return;
    }

    const run = ++runRef.current;
    setModal({ file, duration: check.duration, frames: [], selected: 0, loading: true });

    const captured = await captureFrames(file, check.duration);
    if (run !== runRef.current) return;       // cancelled while preparing

    if (captured.length === 0) {
      setModal(null);
      setError(t("video.errors.noFrames"));
      return;
    }
    setModal({
      file,
      duration: check.duration,
      frames: captured.map((f) => ({ ...f, url: URL.createObjectURL(f.blob) })),
      selected: 0,
      loading: false,
    });
  };

  const confirm = () => {
    const { file, duration, frames, selected } = modal;
    const poster = frames[selected].blob;
    close();
    onConfirm?.({ file, duration, poster });
  };

  return {
    inputRef,
    openPicker: () => inputRef.current?.click(),
    handleFile,
    error,
    modal: (
      <VideoPosterModal
        open={!!modal}
        loading={modal?.loading ?? false}
        frames={modal?.frames ?? []}
        selectedIndex={modal?.selected ?? 0}
        onSelect={(idx) => setModal((m) => (m ? { ...m, selected: idx } : m))}
        onConfirm={confirm}
        onCancel={close}
      />
    ),
  };
}